import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Upload, BarChart3, Users, MapPin, Globe } from 'lucide-react';
import { API, useAuth } from '@/App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const MAX_OPTIONS = 6;
const MIN_OPTIONS = 2;
// Local fallback key (offline / not logged in). Backend drafts take priority
// when the user is authenticated.
const LOCAL_DRAFT_KEY = 'expatssphere_poll_draft';

/**
 * Dialog for creating a poll.
 *
 * Scope is one of: group (auto-approved, scope_id required),
 * connections (auto-approved, visible to creator's contacts),
 * city (requires admin approval until creator has 3 approved city polls).
 *
 * Polls intentionally do NOT include date / time / exact location / tickets.
 * Those are added during the "Convert to Event" step.
 */
const SCOPES = [
  { id: 'group', label: 'A group', icon: Users, hint: 'Only members of the group can see and vote' },
  { id: 'connections', label: 'My connections', icon: Users, hint: 'Only your accepted contacts will see this' },
  { id: 'city', label: 'My city', icon: MapPin, hint: 'Visible to everyone in your city (admin reviews first)' },
];

const CreatePollDialog = ({ open, onOpenChange, defaultScope, defaultGroupId, onCreated, initialDraftId = null }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [myGroups, setMyGroups] = useState([]);
  // Backend draft tracking. We mint a draft_id once per editing session and
  // upsert the whole snapshot via PUT /api/poll-drafts/{id} (debounced).
  // The id is shared with the parent (via `initialDraftId`) so PollsPage can
  // open a specific draft from its "My drafts" list.
  const [draftId, setDraftId] = useState(initialDraftId);

  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState(['', '']);
  // Parallel array of optional datetime-local strings for each option (or empty)
  const [optionDatetimes, setOptionDatetimes] = useState(['', '']);
  const [scopeType, setScopeType] = useState(defaultScope || 'connections');
  const [groupId, setGroupId] = useState(defaultGroupId || '');
  const [imageData, setImageData] = useState(''); // base64 or url
  const [expireDays, setExpireDays] = useState(7);
  const [allowMultipleChoices, setAllowMultipleChoices] = useState(false);
  // Whether we've shown a "Resume draft?" banner this open. Lives only for
  // the lifetime of the dialog so dismissing it doesn't keep flashing.
  const [hasDraftBanner, setHasDraftBanner] = useState(false);
  // Most recent time the draft was auto-saved — drives the "Draft saved" indicator.
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  // Connections sub-mode: 'all' (default broadcast) or 'pick' (hand-pick subset)
  const [connMode, setConnMode] = useState('all');
  const [myContacts, setMyContacts] = useState([]);
  const [pickedIds, setPickedIds] = useState(new Set());
  const [contactSearch, setContactSearch] = useState('');

  // Reset state when dialog opens. Strategy:
  //   1. If a specific backend draft_id was passed, fetch it from the API.
  //   2. Else, fall back to the local-storage draft (offline / unauth).
  //   3. Else, clean state.
  useEffect(() => {
    if (!open) return;

    const hydrateFrom = (parsed) => {
      setHasDraftBanner(true);
      setQuestion(parsed.question || '');
      setDescription(parsed.description || '');
      setOptions(parsed.options?.length >= MIN_OPTIONS ? parsed.options : ['', '']);
      setOptionDatetimes(
        parsed.optionDatetimes?.length === (parsed.options || []).length
          ? parsed.optionDatetimes
          : new Array((parsed.options || ['', '']).length).fill('')
      );
      setScopeType(parsed.scopeType || defaultScope || 'connections');
      setGroupId(parsed.groupId || defaultGroupId || '');
      setImageData(parsed.imageData || '');
      setExpireDays(parsed.expireDays || 7);
      setAllowMultipleChoices(!!parsed.allowMultipleChoices);
      setConnMode(parsed.connMode || 'all');
      setPickedIds(new Set(parsed.pickedIds || []));
    };

    const cleanReset = () => {
      setHasDraftBanner(false);
      setQuestion('');
      setDescription('');
      setOptions(['', '']);
      setOptionDatetimes(['', '']);
      setScopeType(defaultScope || 'connections');
      setGroupId(defaultGroupId || '');
      setImageData('');
      setExpireDays(7);
      setAllowMultipleChoices(false);
      setConnMode('all');
      setPickedIds(new Set());
      setContactSearch('');
      setDraftSavedAt(null);
    };

    setDraftId(initialDraftId);

    if (user && initialDraftId) {
      // Backend draft requested — fetch and hydrate
      axios.get(`${API}/poll-drafts/${initialDraftId}`).then((r) => {
        if (r.data?.payload) hydrateFrom(r.data.payload);
        else cleanReset();
      }).catch(() => {
        toast.error('Could not load that draft');
        cleanReset();
      });
      return;
    }

    // Fallback: local-storage draft
    let draft = null;
    try {
      const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && (parsed.question || (parsed.options || []).some((o) => o && o.trim()))) {
          draft = parsed;
        }
      }
    } catch {
      /* ignore corrupted draft */
    }
    if (draft) hydrateFrom(draft);
    else cleanReset();
  }, [open, defaultScope, defaultGroupId, initialDraftId, user]);

  // Persist a draft on every meaningful change while the dialog is open.
  // When authenticated, we save to the backend (`PUT /api/poll-drafts/{id}`)
  // so drafts sync across devices. Otherwise we fall back to localStorage.
  // We debounce by 800ms so we don't slam the API on every keystroke.
  useEffect(() => {
    if (!open) return;
    const isDirty = question.trim().length > 0 ||
      options.some((o) => (o || '').trim().length > 0 && o !== 'Option 1' && o !== 'Option 2');
    if (!isDirty) {
      // Empty state — clear any leftover local draft so reopening is clean.
      try { localStorage.removeItem(LOCAL_DRAFT_KEY); } catch { /* ignore */ }
      return;
    }
    const snapshot = {
      question,
      description,
      options,
      optionDatetimes,
      scopeType,
      groupId,
      imageData,
      expireDays,
      allowMultipleChoices,
      connMode,
      pickedIds: Array.from(pickedIds),
      savedAt: new Date().toISOString(),
    };

    // Always save locally as a safety net.
    try {
      localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(snapshot));
    } catch { /* ignore */ }

    // Save to backend (debounced) so it syncs across devices.
    if (!user) {
      setDraftSavedAt(new Date());
      return;
    }
    const handle = setTimeout(async () => {
      let id = draftId;
      if (!id) {
        // Mint a stable id; reuse the user's UUID generator via uuid lib alt.
        id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        setDraftId(id);
      }
      try {
        await axios.put(`${API}/poll-drafts/${id}`, { payload: snapshot });
        setDraftSavedAt(new Date());
      } catch (e) {
        // Don't toast on every failure — local copy is still safe.
        // eslint-disable-next-line no-console
        console.warn('Backend draft save failed', e);
      }
    }, 800);
    return () => clearTimeout(handle);
  }, [
    open, question, description, options, optionDatetimes, scopeType, groupId,
    imageData, expireDays, allowMultipleChoices, connMode, pickedIds, user, draftId,
  ]);

  const discardDraft = () => {
    try { localStorage.removeItem(LOCAL_DRAFT_KEY); } catch { /* ignore */ }
    if (user && draftId) {
      axios.delete(`${API}/poll-drafts/${draftId}`).catch(() => { /* idempotent */ });
    }
    setDraftId(null);
    setQuestion('');
    setDescription('');
    setOptions(['', '']);
    setOptionDatetimes(['', '']);
    setScopeType(defaultScope || 'connections');
    setGroupId(defaultGroupId || '');
    setImageData('');
    setExpireDays(7);
    setAllowMultipleChoices(false);
    setConnMode('all');
    setPickedIds(new Set());
    setHasDraftBanner(false);
    setDraftSavedAt(null);
  };

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/groups/my`).then((r) => {
      setMyGroups(r.data?.groups || r.data || []);
    }).catch(() => setMyGroups([]));
  }, [open]);

  // Lazy-load contacts list only when user switches to "pick" mode
  useEffect(() => {
    if (!open || connMode !== 'pick' || myContacts.length > 0) return;
    axios.get(`${API}/connections`).then((r) => {
      setMyContacts(r.data?.contacts || []);
    }).catch(() => setMyContacts([]));
  }, [open, connMode, myContacts.length]);

  const addOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    setOptions([...options, '']);
    setOptionDatetimes([...optionDatetimes, '']);
  };

  const removeOption = (idx) => {
    if (options.length <= MIN_OPTIONS) return;
    setOptions(options.filter((_, i) => i !== idx));
    setOptionDatetimes(optionDatetimes.filter((_, i) => i !== idx));
  };

  const updateOption = (idx, value) => {
    const next = [...options];
    next[idx] = value;
    setOptions(next);
  };

  const updateOptionDatetime = (idx, value) => {
    const next = [...optionDatetimes];
    next[idx] = value;
    setOptionDatetimes(next);
  };

  // Auto-populate the next 3 Saturdays at 7pm — common "let's pick a weekend"
  // shape that takes a poll from blank to usable in one tap. Skips today
  // even if it's a Saturday so the soonest suggestion is ≥7 days out.
  const suggestThreeWeekends = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let cursor = new Date(today);
    while (dates.length < 3) {
      cursor.setDate(cursor.getDate() + 1);
      if (cursor.getDay() === 6) {
        const d = new Date(cursor);
        d.setHours(19, 0, 0, 0);
        const pad = (n) => String(n).padStart(2, '0');
        const iso =
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
          `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        const label = d.toLocaleDateString(undefined, {
          weekday: 'short', month: 'short', day: 'numeric',
        }) + ' evening';
        dates.push({ iso, label });
      }
    }
    setOptions(dates.map((d) => d.label));
    setOptionDatetimes(dates.map((d) => d.iso));
    toast.success('Filled with the next 3 Saturdays — tweak as needed');
  };

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image');
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      const maxDim = 1200;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round(height * (maxDim / width));
          width = maxDim;
        } else {
          width = Math.round(width * (maxDim / height));
          height = maxDim;
        }
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      setImageData(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => toast.error('Image could not be loaded');
    img.src = URL.createObjectURL(file);
  };

  const handleSubmit = async () => {
    const cleanQ = question.trim();
    // Keep options + datetimes aligned by index; only drop pairs where the
    // option text is empty.
    const cleanOpts = [];
    const cleanDts = [];
    options.forEach((o, i) => {
      const t = o.trim();
      if (!t) return;
      cleanOpts.push(t);
      // Convert datetime-local value (no tz) to ISO. Browser values are
      // already in YYYY-MM-DDTHH:MM format; just append seconds for clean ISO.
      const dt = (optionDatetimes[i] || '').trim();
      cleanDts.push(dt ? dt : null);
    });
    if (cleanQ.length < 3) {
      toast.error('Add a clearer question (at least 3 characters)');
      return;
    }
    if (cleanOpts.length < MIN_OPTIONS) {
      toast.error(`Add at least ${MIN_OPTIONS} options`);
      return;
    }
    if (scopeType === 'group' && !groupId) {
      toast.error('Pick a group');
      return;
    }
    if (scopeType === 'connections' && connMode === 'pick' && pickedIds.size === 0) {
      toast.error('Pick at least one connection');
      return;
    }
    if (scopeType === 'city' && !user?.residence_city) {
      toast.error('Set your city in your profile first');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        question: cleanQ,
        options: cleanOpts,
        option_datetimes: cleanDts,
        scope_type: scopeType,
        scope_id: scopeType === 'group' ? groupId : null,
        description: description.trim(),
        image_url: imageData,
        expire_days: parseInt(expireDays, 10) || 7,
        allow_multiple_choices: allowMultipleChoices,
        recipient_user_ids:
          scopeType === 'connections' && connMode === 'pick'
            ? Array.from(pickedIds)
            : [],
      };
      if (scopeType === 'city') {
        payload.city = user?.residence_city || '';
        payload.country = user?.residence_country || '';
      }
      const res = await axios.post(`${API}/polls`, payload);
      // Successful publish — discard any saved draft (both backend + local).
      try { localStorage.removeItem(LOCAL_DRAFT_KEY); } catch { /* ignore */ }
      if (user && draftId) {
        axios.delete(`${API}/poll-drafts/${draftId}`).catch(() => { /* idempotent */ });
      }
      setDraftId(null);
      if (scopeType === 'city' && res.data?.moderation_status === 'pending') {
        toast.success('Poll submitted! An admin will review it shortly.');
      } else {
        toast.success('Poll created');
      }
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not create poll';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="create-poll-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-600" />
            Start a Poll
          </DialogTitle>
          <DialogDescription>
            Gauge interest before committing to a date or location.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {hasDraftBanner && (
            <div
              className="flex items-center gap-3 p-3 bg-amber-50 border-2 border-amber-200 rounded-xl"
              data-testid="poll-draft-banner"
            >
              <span className="text-xl leading-none">📝</span>
              <p className="flex-1 text-xs text-amber-900">
                <strong>Draft restored.</strong> Pick up where you left off — or start fresh.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={discardDraft}
                className="text-amber-700 hover:text-red-600 text-xs h-7"
                data-testid="poll-draft-discard-btn"
              >
                Discard
              </Button>
            </div>
          )}
          {/* Question */}
          <div className="space-y-2">
            <Label htmlFor="poll-question">Question *</Label>
            <Input
              id="poll-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Anyone up for a hike this weekend?"
              maxLength={200}
              data-testid="poll-question-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="poll-description">Description (optional)</Label>
            <Textarea
              id="poll-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A bit more context to help people decide…"
              rows={3}
              maxLength={500}
              data-testid="poll-description-input"
            />
          </div>

          {/* Options */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Options *</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={suggestThreeWeekends}
                  className="text-xs text-violet-600 hover:underline"
                  data-testid="suggest-three-weekends-btn"
                  title="Auto-fill the next 3 Saturday evenings"
                >
                  Suggest 3 weekends
                </button>
                <span className="text-xs text-muted-foreground">
                  {options.length}/{MAX_OPTIONS}
                </span>
              </div>
            </div>
            {options.map((opt, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex items-center gap-2">
                  <Input
                    value={opt}
                    onChange={(e) => updateOption(idx, e.target.value)}
                    placeholder={`Option ${idx + 1}`}
                    maxLength={100}
                    data-testid={`poll-option-input-${idx}`}
                  />
                  {options.length > MIN_OPTIONS && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeOption(idx)}
                      aria-label="Remove option"
                      data-testid={`poll-option-remove-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {/* Optional date/time per option — used to pre-fill the
                    event date when this option wins and gets converted. */}
                <div className="flex items-center gap-2 pl-1">
                  <input
                    type="datetime-local"
                    value={optionDatetimes[idx] || ''}
                    onChange={(e) => updateOptionDatetime(idx, e.target.value)}
                    className="text-xs text-muted-foreground border-0 bg-transparent p-0 focus:ring-0"
                    data-testid={`poll-option-datetime-${idx}`}
                  />
                  {optionDatetimes[idx] && (
                    <button
                      type="button"
                      onClick={() => updateOptionDatetime(idx, '')}
                      className="text-xs text-violet-600 hover:underline"
                      data-testid={`poll-option-datetime-clear-${idx}`}
                    >
                      clear
                    </button>
                  )}
                </div>
              </div>
            ))}
            {options.length < MAX_OPTIONS && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addOption}
                className="text-violet-600"
                data-testid="poll-add-option-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add option
              </Button>
            )}
          </div>

          {/* Image */}
          <div className="space-y-2">
            <Label>Image (optional)</Label>
            {imageData ? (
              <div className="relative">
                <img src={imageData} alt="Poll" className="w-full max-h-48 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => setImageData('')}
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"
                  aria-label="Remove image"
                  data-testid="poll-image-remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg py-6 text-center text-sm text-muted-foreground hover:border-violet-400 hover:text-violet-600 transition-colors"
                data-testid="poll-image-upload-btn"
              >
                <Upload className="w-5 h-5 mx-auto mb-1" />
                Click to upload
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageFile}
              className="hidden"
            />
          </div>

          {/* Scope */}
          <div className="space-y-2">
            <Label>Audience *</Label>
            <div className="grid grid-cols-1 gap-2">
              {SCOPES.map((s) => {
                const Icon = s.icon;
                const active = scopeType === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setScopeType(s.id)}
                    className={`flex items-start gap-3 p-3 border-2 rounded-xl text-left transition-colors ${
                      active
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`poll-scope-${s.id}`}
                  >
                    <Icon className={`w-5 h-5 mt-0.5 ${active ? 'text-violet-600' : 'text-gray-500'}`} />
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{s.label}</p>
                      <p className="text-xs text-muted-foreground">{s.hint}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Connections sub-mode picker */}
          {scopeType === 'connections' && (
            <ConnectionsPicker
              mode={connMode}
              onModeChange={setConnMode}
              contacts={myContacts}
              pickedIds={pickedIds}
              onPickedChange={setPickedIds}
              search={contactSearch}
              onSearchChange={setContactSearch}
            />
          )}

          {/* Group picker */}
          {scopeType === 'group' && (
            <div className="space-y-2">
              <Label htmlFor="poll-group">Pick a group</Label>
              <select
                id="poll-group"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="poll-group-select"
              >
                <option value="">— Choose —</option>
                {myGroups.map((g) => (
                  <option key={g.group_id || g.slug} value={g.group_id}>
                    {g.name}
                  </option>
                ))}
              </select>
              {myGroups.length === 0 && (
                <p className="text-xs text-muted-foreground">You aren't in any groups yet.</p>
              )}
            </div>
          )}

          {/* City info */}
          {scopeType === 'city' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
              <Globe className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                City polls go to admin review to prevent spam. Once you have 3 approved
                city polls, future ones publish instantly.
              </span>
            </div>
          )}

          {/* Expiry */}
          <div className="space-y-2">
            <Label htmlFor="poll-expire">Closes in</Label>
            <select
              id="poll-expire"
              value={expireDays}
              onChange={(e) => setExpireDays(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="poll-expire-select"
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>

          {/* Multi-choice toggle */}
          <label
            className="flex items-start gap-3 p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-violet-300"
            data-testid="multi-choice-toggle-row"
          >
            <input
              type="checkbox"
              checked={allowMultipleChoices}
              onChange={(e) => setAllowMultipleChoices(e.target.checked)}
              className="mt-0.5 rounded border-gray-300"
              data-testid="allow-multiple-choices-checkbox"
            />
            <div className="flex-1">
              <p className="font-semibold text-sm">Allow multiple choices</p>
              <p className="text-xs text-muted-foreground">
                Voters can pick more than one option (e.g., "all dates I'm free for").
                When off, picking a new option replaces the previous vote.
              </p>
            </div>
          </label>

          <div className="flex flex-col gap-2 pt-2">
            {draftSavedAt && (
              <p
                className="text-xs text-emerald-600 flex items-center gap-1"
                data-testid="draft-saved-indicator"
              >
                <span aria-hidden>✓</span>
                Draft auto-saved — close anytime to resume later
              </p>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="flex-1 rounded-full"
                data-testid="poll-cancel-btn"
                title="Your draft will be saved"
              >
                Save &amp; close
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700"
                data-testid="poll-submit-btn"
              >
                {submitting ? 'Publishing…' : 'Publish Poll'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePollDialog;

// ---------------------------------------------------------------------------
// ConnectionsPicker: shown when scope_type=connections. Two modes:
//   - "all"  → broadcast to every accepted contact (default).
//   - "pick" → multi-select subset, with a chip row of cities the user's
//              connections are in for quick "all in Budapest" selection.
// ---------------------------------------------------------------------------
function ConnectionsPicker({
  mode,
  onModeChange,
  contacts,
  pickedIds,
  onPickedChange,
  search,
  onSearchChange,
}) {
  // Cities that at least one connection lives in — for the quick-chip row
  const cityCounts = (() => {
    const m = new Map();
    (contacts || []).forEach((c) => {
      const city = c.residence_city;
      if (!city) return;
      m.set(city, (m.get(city) || 0) + 1);
    });
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  })();

  const filtered = (contacts || []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.residence_city || '').toLowerCase().includes(q)
    );
  });

  const toggle = (uid) => {
    const next = new Set(pickedIds);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    onPickedChange(next);
  };

  const selectAllInCity = (city) => {
    const next = new Set(pickedIds);
    (contacts || []).forEach((c) => {
      if (c.residence_city === city) next.add(c.user_id);
    });
    onPickedChange(next);
  };

  return (
    <div className="space-y-3 pl-4 border-l-2 border-violet-200" data-testid="connections-picker">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onModeChange('all')}
          className={`flex-1 text-sm py-2 px-3 rounded-lg border-2 transition-colors ${
            mode === 'all'
              ? 'border-violet-500 bg-violet-50 font-semibold'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          data-testid="conn-mode-all"
        >
          All connections
        </button>
        <button
          type="button"
          onClick={() => onModeChange('pick')}
          className={`flex-1 text-sm py-2 px-3 rounded-lg border-2 transition-colors ${
            mode === 'pick'
              ? 'border-violet-500 bg-violet-50 font-semibold'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          data-testid="conn-mode-pick"
        >
          Pick specific people
        </button>
      </div>

      {mode === 'pick' && (
        <>
          {contacts.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">
              Loading your connections…
            </p>
          )}

          {contacts.length > 0 && (
            <>
              {/* City quick-chip row */}
              {cityCounts.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="city-chips">
                  <span className="text-xs text-muted-foreground self-center mr-1">
                    Quick add:
                  </span>
                  {cityCounts.map(([city, count]) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => selectAllInCity(city)}
                      className="text-xs px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
                      data-testid={`city-chip-${city}`}
                    >
                      All in {city} ({count})
                    </button>
                  ))}
                </div>
              )}

              <input
                type="text"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search by name or city…"
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="contact-search-input"
              />

              <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {filtered.length === 0 && (
                  <p className="text-xs text-muted-foreground p-3 text-center">
                    No connections match.
                  </p>
                )}
                {filtered.map((c) => {
                  const checked = pickedIds.has(c.user_id);
                  return (
                    <label
                      key={c.user_id}
                      className={`flex items-center gap-3 p-2.5 cursor-pointer text-sm ${
                        checked ? 'bg-violet-50' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`contact-row-${c.user_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(c.user_id)}
                        className="rounded border-gray-300"
                      />
                      {c.profile_picture ? (
                        <img src={c.profile_picture} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center font-semibold text-xs text-gray-700 flex-shrink-0">
                          {(c.name || '?')[0]}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{c.name}</p>
                        {c.residence_city && (
                          <p className="text-xs text-muted-foreground truncate">{c.residence_city}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground" data-testid="picked-count">
                  {pickedIds.size} selected
                </span>
                {pickedIds.size > 0 && (
                  <button
                    type="button"
                    onClick={() => onPickedChange(new Set())}
                    className="text-violet-600 hover:underline"
                    data-testid="clear-picked"
                  >
                    Clear selection
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

